import { useEffect, useMemo } from "react";
import { feedArticles, catStyle, type Article } from "@/mocks/feed";

interface Props {
  article: Article | null;
  onClose: () => void;
  onRead: (a: Article) => void;
}

export default function ReadingModal({ article, onClose, onRead }: Props) {
  const related = useMemo(() => {
    if (!article) return [];
    return feedArticles.filter((a) => a.category === article.category && a.id !== article.id).slice(0, 3);
  }, [article]);

  useEffect(() => {
    if (!article) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [article, onClose]);

  if (!article) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center sm:items-center" role="dialog" aria-modal="true" aria-label={article.title}>
      <button type="button" onClick={onClose} aria-label="关闭" className="absolute inset-0 bg-black/50 cursor-pointer"></button>

      <div className="relative z-10 flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-t-3xl bg-background-50 sm:rounded-3xl animate-pop-in">
        <div className="flex items-center justify-between border-b border-background-200 px-5 py-3">
          <div className="flex items-center gap-2 text-xs text-foreground-500">
            <span className={`rounded-md px-2 py-0.5 text-xs font-medium ${catStyle[article.category]}`}>{article.category}</span>
            <span className="flex items-center gap-1"><i className="ri-eye-line" aria-hidden="true"></i>{article.views}</span>
            <span>{article.time}</span>
          </div>
          <button type="button" onClick={onClose} aria-label="关闭" className="flex h-8 w-8 items-center justify-center rounded-full bg-background-100 text-foreground-700 hover:bg-background-200 cursor-pointer">
            <i className="ri-close-line text-lg" aria-hidden="true"></i>
          </button>
        </div>

        <div className="overflow-y-auto">
          <div className="relative aspect-[16/8] w-full">
            <img src={article.image} alt={article.title} className="h-full w-full object-cover object-top" />
            <div className="absolute inset-0 bg-gradient-to-t from-background-50 to-transparent"></div>
          </div>

          <div className="px-5 pb-8 md:px-8">
            <h3 className="-mt-2 relative text-xl font-display leading-snug text-foreground-950 md:text-2xl">{article.title}</h3>
            <div className="mt-2 flex items-center gap-2 text-xs text-foreground-500">
              <i className="ri-edit-box-line text-primary-600" aria-hidden="true"></i>
              来源：{article.source}
            </div>

            <div className="mt-4 space-y-4">
              {article.content.map((p, i) => (
                <p key={i} className="text-[15px] leading-7 text-foreground-700">{p}</p>
              ))}
            </div>

            <div className="mt-5 rounded-2xl border border-background-200 bg-background-100 p-4 text-xs leading-relaxed text-foreground-500">
              本页面为全网公开热点信息的聚合梳理，仅供参考。事件细节请以权威媒体报道与官方通报为准。
            </div>

            {related.length > 0 && (
              <div className="mt-6">
                <h4 className="font-display text-base text-foreground-950">相关推荐</h4>
                <ul className="mt-3 space-y-2">
                  {related.map((r) => (
                    <li key={r.id}>
                      <button
                        type="button"
                        onClick={() => onRead(r)}
                        className="group flex w-full items-start gap-3 rounded-xl border border-background-200 bg-background-50 p-3 text-left hover:border-primary-200 cursor-pointer transition-colors"
                      >
                        <img src={r.image} alt="" loading="lazy" className="h-14 w-20 shrink-0 rounded-lg object-cover object-top" />
                        <div className="min-w-0">
                          <p className="line-clamp-2 text-sm font-medium text-foreground-900 group-hover:text-primary-700 transition-colors">{r.title}</p>
                          <p className="mt-1 text-xs text-foreground-500">{r.category} · {r.time}</p>
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}